import {
  AddCircle,
  BorderColor,
  Description,
  DocumentScanner,
  ExpandMore,
  LocalPrintshop,
  MoreVert,
  Print,
  Save,
  Search,
  Visibility,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Menu,
  Pagination,
  TextField,
  Typography,
} from "@mui/material";
import CarService from "_api/Car/buyCar";
import Actions from "components/Car/Actions";
import TableData from "components/Car/Table";
import ChipCustomize from "components/Chip";
import CustomizeDatePicker from "components/DatePicker";
import {
  GradientButton,
  GradientButtonOutline,
} from "components/GradientButton";
import ModalCreateTransferAndReceiptBuy from "components/Modal/Car/FormDocuments/TransferAndReceiptBuy";
import ModalBuyCar from "components/Modal/Car/ModalBuyCar";
import ModalDetailCar from "components/Modal/Car/ModalDetailCar";
import CustomPagination from "components/TableExplanded/Pagination";
import { filter, set } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateTime } from "utils/helpers/format";

const BuyCars = () => {
  const [carsList, setCarsList] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    // Fetch data when the component mounts
    fetchData();
  }, [pagination.page, pagination.limit]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Simulate fetching data
      const response = await CarService.getCarBuyAllList({
        page: pagination.page || 1,
        limit: pagination.limit || 10,
        search: searchQuery ?? "",
        date: searchDate ?? "",
      });
      setCarsList(response.data);
      setPagination(response.meta);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchQuery || searchDate) {
      if (pagination.page !== 1) {
        setPagination((prev) => ({
          ...prev,
          page: 1, // Reset to first page when searching
        }));
      } else {
        fetchData();
      }
    }
  };

  const handleClearSearch = async () => {
    setSearchQuery("");
    setSearchDate("");
    if (pagination.page !== 1) {
      setPagination((prev) => ({
        ...prev,
        page: 1, // Reset to first page when clearing search
      }));
    } else {
      setLoading(true);
      try {
        const response = await CarService.getCarBuyAllList({
          page: 1,
          limit: pagination.limit || 10,
          search: "",
          date: "",
        });
        setCarsList(response.data);
        setPagination(response.meta);
      } catch (error) {
        console.error("Error clearing search:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCreateModalOpen = () => {
    setIsCreateModalOpen(true);
  };
  const handleCreateModalClose = () => {
    setIsCreateModalOpen(false);
    if (isEditMode) {
      setIsEditMode(false); // Reset edit mode when closing the modal
      setSelectedRow(null); // Clear selected row when closing
    }
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({
      ...prev,
      page: value + 1,
    }));
  };
  const handleRowsPerPageChange = (event) => {
    setPagination((prev) => ({
      ...prev,
      limit: event.target.value,
      page: 1, // Reset to first page when changing rows per page
    }));
  };

  const handleRowClick = (row, type = "detail") => {
    setSelectedRow(row);
    if (type === "detail") {
      // Handle row click for detail view
      console.log("Row clicked for detail:", row);
      setIsDetailModalOpen(true);
    } else if (type === "edit") {
      // Handle row click for edit view
      console.log("Row clicked for edit:", row);
      setIsEditMode(true);
      setIsCreateModalOpen(true); // Open the detail modal in edit mode
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography component={"div"} sx={{ fontSize: "32px" }}>
          รถที่ซื้อ
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* Add any additional buttons or actions here */}
          <GradientButtonOutline
            onClick={() => {
              // Handle the click event for adding a new car
            }}
            sx={{
              px: 3,
              display: "flex",
              alignItems: "center",
              height: "48px",
              backgroundColor: "white",
            }}
          >
            <Typography style={{ marginRight: "8px" }}>ส่งออก Excel</Typography>
            <Save fontSize="medium" />
          </GradientButtonOutline>
          <GradientButton
            onClick={() => {
              // Handle the click event for saving
              handleCreateModalOpen();
            }}
            sx={{
              px: 3,
              display: "flex",
              alignItems: "center",
              height: "48px",
            }}
          >
            <Typography style={{ marginRight: "8px" }}>ซื้อรถ</Typography>
            <AddCircle fontSize="medium" />
          </GradientButton>
        </Box>
      </Box>

      {/* searchQuery component can be added here if needed */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          justifyContent: "flex-end",
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              whiteSpace: "nowrap",
            }}
          >
            ค้นหาทั้งหมด:
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="ค้นหา"
            sx={{
              flex: 3,
              "& .MuiOutlinedInput-root": {
                bgcolor: "white",
                height: "40px",
              },
            }}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              whiteSpace: "nowrap",
            }}
          >
            วันที่:
          </Typography>
          <CustomizeDatePicker
            value={searchDate}
            onChange={(date) => {
              setSearchDate(date);
            }}
            disableFuture={true}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            alignItems: "center",
          }}
        >
          <GradientButtonOutline
            onClick={() => {
              // Handle clear action
              handleClearSearch();
            }}
            sx={{
              height: "40px",
            }}
          >
            ล้าง
          </GradientButtonOutline>
          <GradientButton
            onClick={() => {
              // Handle search action
              handleSearch();
            }}
            endIcon={<Search />}
          >
            ค้นหา
          </GradientButton>
        </Box>
      </Box>

      {/* Table or list to display the cars */}
      <TableData
        loading={loading}
        data={carsList}
        columns={[
          {
            key: "expand",
            label: "",
            style: {
              textAlign: "center",
            },
          },
          {
            key: "car_pictures",
            label: "รูปภาพ",
          },
          { key: "car_code", label: "รหัสรถ" },
          { key: "license_plate", label: "ทะเบียน" },
          { key: "car_brand", label: "ยี่ห้อ" },
          { key: "car_model", label: "รุ่น" },
          {
            key: "created_at",
            label: "วันที่ซื้อเข้า",
            render: (row) => formatDateTime(row.created_at),
          },
          {
            key: "status_name",
            label: "สถานะ",
            render: (row) => {
              return (
                <ChipCustomize
                  label={row.status_name}
                  status={row.status_name}
                />
              );
            },
          },
          {
            key: "action",
            label: "Actions",
            style: {
              textAlign: "center",
            },
            render: (row) => (
              <Actions
                menuList={[
                  {
                    label: "ดูรายละเอียด",
                    icon: <Visibility />,
                    onClick: () => {
                      handleRowClick(row, "detail");
                    },
                  },
                  ...(row.status_name !== "ขาย"
                    ? [
                        {
                          label: "แก้ไข",
                          icon: <BorderColor />,
                          onClick: () => handleRowClick(row, "edit"),
                        },
                      ]
                    : []),
                  {
                    label: "พิมพ์ใบรับซื้อ",
                    icon: <LocalPrintshop />,
                    onClick: () => {
                      console.log("Print clicked for row:", row);
                    },
                  },
                  {
                    label: "ฟอร์มเอกสาร",
                    icon: <Description />,
                    onClick: () => {
                      console.log("Save clicked for row:", row);
                      navigate(`/car/buy_cars/form/${row.car_id}`);
                    },
                  },
                ]}
              />
            ),
          },
        ]}
      />

      <CustomPagination
        totalPage={pagination.totalPages}
        onPageChange={(event, value) => {
          handlePageChange(event, value);
        }}
        page={pagination.page - 1}
        rowsPerPage={pagination.limit}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      <ModalBuyCar
        open={isCreateModalOpen}
        onClose={handleCreateModalClose}
        onSaved={() => {
          fetchData(1, pagination.limit); // Refresh the data after saving
        }}
        isEdit={isEditMode}
        id={selectedRow ? selectedRow.car_id : null}
      />

      <ModalDetailCar
        open={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedRow(null); // Clear selected row when closing
        }}
        rowID={selectedRow ? selectedRow.car_id : null}
        type="buy_car"
        formlist={[
          {
            id: 1,
            document: "แบบคำขอโอนและรับโอน",
            document_type: "car_transform",
          },
          {
            id: 2,
            document: "หนังสือมอบอำนาจ",
            document_type: "car_power_of_attorney",
          },
          {
            id: 3,
            document: "หนังสือสัญญาซื้อ - ขายรถ",
            document_type: "car_sell_contact",
          },
        ]}
        fetchList={fetchData}
      />
    </Box>
  );
};

export default BuyCars;
